import React, { useEffect, useRef } from 'react';

export default function NeuralCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let raf;
    let nodes = [];
    let scrollY = window.scrollY;
    let targetEnergy = 0;
    let energy = 0;
    let mouseX = -9999, mouseY = -9999;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
      seed();
    };

    const seed = () => {
      const count = Math.min(70, Math.floor((window.innerWidth * window.innerHeight) / 28000));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: 0.7 + Math.random() * 1.6,
        phase: Math.random() * Math.PI * 2,
      }));
    };

    const isDark = () => document.documentElement.getAttribute('data-theme') !== 'light';

    const onScroll = () => {
      const dy = window.scrollY - scrollY;
      scrollY = window.scrollY;
      targetEnergy = Math.min(1, targetEnergy + Math.abs(dy) * 0.012);
    };
    const onMove = (e) => { mouseX = e.clientX; mouseY = e.clientY; };

    const draw = () => {
      const W = window.innerWidth, H = window.innerHeight;
      energy += (targetEnergy - energy) * 0.06;
      targetEnergy *= 0.92;
      ctx.clearRect(0, 0, W, H);

      const dark = isDark();
      const nodeBase = dark ? 235 : 30;
      const lineRgb = dark ? '245, 245, 245' : '12, 12, 12';
      const t = performance.now() * 0.0006;

      for (const n of nodes) {
        n.x += n.vx + Math.sin(t + n.phase) * 0.05;
        n.y += n.vy + Math.cos(t + n.phase) * 0.05;
        if (n.x < -10) n.x = W + 10;
        if (n.x > W + 10) n.x = -10;
        if (n.y < -10) n.y = H + 10;
        if (n.y > H + 10) n.y = -10;

        const mdx = mouseX - n.x, mdy = mouseY - n.y;
        const md2 = mdx * mdx + mdy * mdy;
        if (md2 < 160 * 160) {
          const f = (1 - md2 / (160 * 160)) * 0.4;
          n.x += mdx * 0.0006 * f;
          n.y += mdy * 0.0006 * f;
        }

        const a = 0.14 + energy * 0.6;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${nodeBase}, ${nodeBase}, ${nodeBase}, ${a})`;
        ctx.fill();
      }

      const maxDist = 130;
      const lineBase = lineRgb.split(',').map(v => parseInt(v.trim(), 10));
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < maxDist * maxDist) {
            const d = Math.sqrt(d2);
            const alpha = (1 - d / maxDist) * (dark ? 0.22 : 0.18) * (0.5 + energy * 0.8);
            const cmx = (a.x + b.x) / 2 - mouseX, cmy = (a.y + b.y) / 2 - mouseY;
            const md = Math.sqrt(cmx * cmx + cmy * cmy);
            const mInfluence = Math.max(0, 1 - md / 200);
            const r = mInfluence > 0.05
              ? `${Math.round(lineBase[0] * (1 - mInfluence) + 103 * mInfluence)}, ${Math.round(lineBase[1] * (1 - mInfluence) + 89 * mInfluence)}, ${Math.round(lineBase[2] * (1 - mInfluence) + 255 * mInfluence)}`
              : lineRgb;
            ctx.strokeStyle = `rgba(${r}, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMove);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  return <canvas ref={ref} className="neural-canvas" />;
}
